/**
 * getErrorMessage — normalize any API/network error into a safe display
 * string suitable for toasts, alerts, inline error slots, etc.
 *
 * Why this exists:
 *   Some backend endpoints (e.g. abuse-shield) return
 *     `detail: { error: "blocked", reason_code: "abuse_shield" }`
 *   FastAPI happily serializes that dict. If the frontend passes
 *   `err.response.data.detail` straight into JSX / toast / `setState`,
 *   React crashes with "Objects are not valid as a React child".
 *
 *   NEVER pass `err.response.data.detail` directly to a toast or
 *   React text node — always route through this helper.
 *
 * Usage:
 *   try { await api.post(...) }
 *   catch (err) { toast.error(getErrorMessage(err, "Login failed")); }
 */

const REASON_CODE_COPY = {
  abuse_shield:
    "Too many failed attempts from this network. Please wait a few minutes and try again.",
  rate_limited:
    "You're going a little fast. Please slow down and retry in a moment.",
};

export function getErrorMessage(err, fallback = "Something went wrong.") {
  try {
    if (err == null) return fallback;
    if (typeof err === "string") return err;

    // Axios-style error with a response body
    const data = err?.response?.data;
    const detail = data?.detail;

    // Backend sometimes returns a dict detail ({error, reason_code, …}).
    // Prefer a human-friendly reason_code translation, else show the
    // generic .error string, else fall back to the raw JSON.
    if (detail && typeof detail === "object") {
      if (typeof detail.reason_code === "string" && REASON_CODE_COPY[detail.reason_code]) {
        return REASON_CODE_COPY[detail.reason_code];
      }
      if (typeof detail.error === "string") return detail.error;
      if (typeof detail.message === "string") return detail.message;
      if (typeof detail.reason_code === "string") return detail.reason_code;
    }

    // Standard FastAPI string detail
    if (typeof detail === "string") return detail;

    // Top-level fields some endpoints return
    if (typeof data?.message === "string") return data.message;
    if (typeof data?.error === "string") return data.error;

    // Axios' own message
    if (typeof err?.message === "string" && err.message) return err.message;

    return fallback;
  } catch {
    return fallback;
  }
}

export default getErrorMessage;
